import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useDraggable } from "@dnd-kit/core";
import type {
  DayBlockSegment,
  POI,
  ScheduledPOI,
  Tag,
} from "@poi-planner/shared";
import { useTripStore } from "../../store/tripStore";
import { useUIStore } from "../../store/uiStore";
import { Button } from "../Button";
import { TagIcon } from "../TagIcon";
import {
  eventDisplayTimeInfo,
  minutesBetween,
  zonedTimeOfDay,
} from "../../utils/zonedTime";
import { requiresCrossZoneGestureConfirmation } from "../../utils/calendarGestures";
import { checkConstraintViolations } from "../../utils/constraints";
import { dndIds } from "../../utils/dndIds";
import type { BlockGeometry } from "../../utils/timeGrid";
import type { BlockLayout } from "../../utils/calendarLayout";
import type { BoardDragData } from "./dragTypes";
import { useBlockResize } from "./useBlockResize";
import { ResizeHandles } from "./ResizeHandles";
import { useLockedDragProbe } from "./useLockedDragProbe";
import { CalendarActionToolbar } from "./CalendarActionToolbar";
import { useCalendarToolbarHover } from "./useCalendarToolbarHover";
import { useCalendarDndStore } from "../../store/calendarDndStore";

interface CalendarBlockProps {
  scheduledPOI: ScheduledPOI;
  poi: POI;
  primaryTag: Tag | undefined;
  geometry: BlockGeometry;
  layout: BlockLayout;
  /** Column date (YYYY-MM-DD) for constraint checks */
  date: string;
  /** Column IANA zone (resize math happens in column space) */
  zone: string;
  /** Left accent color (accommodation palette text or neutral) */
  accentColor: string;
  isReadOnly: boolean;
  segment: DayBlockSegment;
  /** Selects this located POI in the trip sidebar and any visible map. */
  onShowOnMap?: () => void;
}

/**
 * A scheduled POI visit on the time grid. Draggable to new times/days (span
 * preserved), edge-resizable, with hover/tap actions. Locked blocks refuse
 * drags with the locked prompt and hide their handles.
 */
function CalendarBlockComponent({
  scheduledPOI,
  poi,
  primaryTag,
  geometry,
  layout,
  date,
  zone,
  accentColor,
  isReadOnly,
  segment,
  onShowOnMap,
}: CalendarBlockProps) {
  const {
    hovered,
    onMouseEnter: handleToolbarMouseEnter,
    onMouseLeave: handleToolbarMouseLeave,
  } = useCalendarToolbarHover();
  const [pinnedActions, setPinnedActions] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);
  const wasDragging = useRef(false);
  const pointerType = useRef("mouse");

  const unschedulePOI = useTripStore((s) => s.unschedulePOI);
  const updateScheduledPoi = useTripStore((s) => s.updateScheduledPoi);
  const setScheduledPoiLocked = useTripStore((s) => s.setScheduledPoiLocked);
  const openScheduledVisitEditor = useUIStore(
    (s) => s.openScheduledVisitEditor,
  );
  const setResizePreview = useCalendarDndStore((s) => s.setResizePreview);

  const locked = scheduledPOI.locked;
  const spanMinutes = Math.max(
    Math.round(minutesBetween(scheduledPOI.startTime, scheduledPOI.endTime)),
    0,
  );

  const {
    preview: resizePreview,
    resizing,
    handleProps,
  } = useBlockResize({
    startTime: scheduledPOI.startTime,
    endTime: scheduledPOI.endTime,
    date,
    zone,
    disabled: locked || isReadOnly,
    onCommit: (times, context) => {
      if (
        requiresCrossZoneGestureConfirmation(
          context.originalDisplayZone,
          context.targetDisplayZone,
        )
      ) {
        openScheduledVisitEditor(scheduledPOI.id, {
          eventType: "scheduledVisit",
          entityId: scheduledPOI.id,
          gesture: "resize",
          ...times,
          originalDisplayZone: context.originalDisplayZone,
          targetDisplayZone: context.targetDisplayZone,
        });
        return;
      }
      updateScheduledPoi(scheduledPOI.id, times);
    },
    onPreviewChange: setResizePreview,
  });

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dndIds.calendarPoiSegment(scheduledPOI.id, date),
    data: {
      type: "scheduled-poi",
      scheduledPoiId: scheduledPOI.id,
      poiId: poi.id,
      startTime: scheduledPOI.startTime,
      originDisplayDate: date,
      originDisplayZone: zone,
      spanMinutes,
      label: poi.name,
    } satisfies BoardDragData,
    disabled: locked || isReadOnly || resizing || !segment.isStart,
  });

  const setBlockNode = useCallback(
    (node: HTMLDivElement | null) => {
      blockRef.current = node;
      setNodeRef(node);
    },
    [setNodeRef],
  );

  // Attempted drag on a locked block → locked prompt (drag itself is disabled)
  const lockedProbeProps = useLockedDragProbe(
    locked && !isReadOnly,
    "Unlock this scheduled POI before changing its day or time.",
  );

  useEffect(() => {
    if (isDragging) wasDragging.current = true;
  }, [isDragging]);

  const violations = useMemo(
    () =>
      checkConstraintViolations(
        poi,
        date,
        zonedTimeOfDay(scheduledPOI.startTime),
      ),
    [poi, date, scheduledPOI.startTime],
  );

  const displayStart = resizePreview?.startTime ?? scheduledPOI.startTime;
  const displayEnd = resizePreview?.endTime ?? scheduledPOI.endTime;
  const timeInfo = useMemo(
    () =>
      eventDisplayTimeInfo(displayStart, displayEnd, zone, {
        clippedTop: geometry.clippedTop,
        clippedBottom: geometry.clippedBottom,
      }),
    [
      displayStart,
      displayEnd,
      zone,
      geometry.clippedTop,
      geometry.clippedBottom,
    ],
  );

  const top = resizePreview?.top ?? geometry.top;
  const height = resizePreview?.height ?? geometry.height;
  const compact = height < 40;

  const highlighted = hovered || pinnedActions || resizing;
  const showActions = !isReadOnly && !resizing && highlighted;
  const showHandles = !isReadOnly && !locked && highlighted;

  // Mouse/keyboard clicks mirror note blocks by opening the editor. Touch and
  // pen taps pin the action cluster because those inputs have no reliable hover.
  // Clicks that end a drag are ignored.
  const handleClick = () => {
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
    if (
      !isReadOnly &&
      (pointerType.current === "touch" || pointerType.current === "pen")
    ) {
      setPinnedActions((pinned) => !pinned);
      return;
    }

    setPinnedActions(false);
    openScheduledVisitEditor(scheduledPOI.id);
  };

  return (
    <div
      ref={setBlockNode}
      {...attributes}
      {...listeners}
      {...lockedProbeProps}
      onPointerDownCapture={(event) => {
        pointerType.current = event.pointerType;
      }}
      onClick={handleClick}
      className={clsx(
        "group absolute flex flex-col gap-px rounded-lg px-2 pt-1 pb-0.5 shadow-sm",
        "border border-border transition-colors",
        highlighted ? "bg-sky-50" : "bg-surface",
        locked
          ? "cursor-default border-indigo-200 ring-2 ring-indigo-100"
          : segment.isStart
            ? "cursor-grab"
            : "cursor-pointer",
        showActions || showHandles ? "overflow-visible" : "overflow-hidden",
        isDragging && "opacity-40",
        resizing && "select-none",
      )}
      style={{
        top,
        height,
        left: `calc(8px + ${layout.leftPct}%)`,
        right: 8,
        borderLeft: `4px solid ${locked ? "var(--color-indigo-500)" : accentColor}`,
        zIndex: layout.zIndex,
        boxShadow:
          layout.depth > 0 ? "0 6px 16px rgba(15, 23, 42, 0.16)" : undefined,
      }}
      onMouseEnter={handleToolbarMouseEnter}
      onMouseLeave={handleToolbarMouseLeave}
      data-scheduled-poi-id={scheduledPOI.id}
      title={timeInfo.tooltip}
    >
      {showHandles && (
        <ResizeHandles
          handleProps={handleProps}
          showStart={segment.isStart}
          showEnd={segment.isEnd}
        />
      )}

      {/* Hover/tap actions */}
      {showActions && (
        <CalendarActionToolbar
          anchorRef={blockRef}
          ariaLabel={`Actions for ${poi.name}`}
          onMouseEnter={handleToolbarMouseEnter}
          onMouseLeave={handleToolbarMouseLeave}
        >
          {onShowOnMap && (
            <Button
              variant="icon"
              size="sm"
              icon="location_searching"
              aria-label={`Show ${poi.name} on map`}
              title="Show on map"
              onClick={onShowOnMap}
            />
          )}
          <Button
            variant="icon"
            size="sm"
            icon="edit"
            aria-label={`Edit scheduled visit to ${poi.name}`}
            title="Edit visit"
            onClick={() => openScheduledVisitEditor(scheduledPOI.id)}
          />
          <Button
            variant="icon"
            size="sm"
            icon={locked ? "lock_open" : "lock"}
            aria-label={locked ? "Unlock time" : "Lock time"}
            title={locked ? "Unlock time" : "Lock time"}
            onClick={() => setScheduledPoiLocked(scheduledPOI.id, !locked)}
          />
          <Button
            variant="icon"
            size="sm"
            icon="close"
            aria-label={`Unschedule ${poi.name}`}
            title="Unschedule"
            onClick={() => unschedulePOI(scheduledPOI.id)}
          />
        </CalendarActionToolbar>
      )}

      <div className="flex items-center gap-1 text-2xs font-bold text-secondary tabular-nums">
        {primaryTag && <TagIcon icon={primaryTag.icon} size={11} />}
        <span className="truncate">{timeInfo.displayRange}</span>
        {timeInfo.nativeRange && (
          <span
            className="material-symbols-outlined text-xs leading-none text-info"
            title={`Native time: ${timeInfo.nativeRange}`}
            aria-label={`Native time: ${timeInfo.nativeRange}`}
          >
            public
          </span>
        )}
        <span className="flex-1" />
        {violations.length > 0 && (
          <span
            className="material-symbols-outlined text-xs leading-none text-warning"
            title={violations.map((violation) => violation.message).join("\n")}
          >
            warning
          </span>
        )}
        {locked && (
          <span
            className="material-symbols-outlined text-xs leading-none text-tertiary"
            title="Time locked"
          >
            lock
          </span>
        )}
      </div>
      {!compact && (
        <>
          {timeInfo.nativeRange && height >= 56 && (
            <div className="truncate text-2xs font-semibold text-blue-700">
              {timeInfo.nativeRange}
            </div>
          )}
          <div className="truncate text-xs font-bold leading-tight tracking-tight">
            {poi.name}
          </div>
        </>
      )}
      {compact && <span className="sr-only">{poi.name}</span>}

      {geometry.clippedBottom && !resizing && (
        <span
          className="material-symbols-outlined absolute bottom-0 left-1/2 -translate-x-1/2 text-xs leading-none text-tertiary"
          title="Continues past midnight"
        >
          keyboard_double_arrow_down
        </span>
      )}
      {geometry.clippedTop && !resizing && (
        <span
          className="material-symbols-outlined absolute top-0 left-1/2 -translate-x-1/2 text-xs leading-none text-tertiary"
          title="Continues from the previous day"
        >
          keyboard_double_arrow_up
        </span>
      )}
    </div>
  );
}

export const CalendarBlock = memo(CalendarBlockComponent);
CalendarBlock.displayName = "CalendarBlock";
